import { ComponentType, SVGProps } from "react";
import { IconType } from "react-icons";
import { GiDesertEagle } from "react-icons/gi";
import { IoTrophy } from "react-icons/io5";
import { FaDiscord, FaSteam } from "react-icons/fa";
// Solid set, to match the site's own icon weight.
import {
  HiBookOpen,
  HiStar,
  HiCollection,
  HiFilm,
  HiGift,
  HiGlobeAlt,
  HiUser,
  HiPlusCircle,
  HiCode,
  HiPencilAlt,
  HiShare,
  HiQuestionMarkCircle,
  HiSparkles,
  HiUserGroup,
  HiVideoCamera,
} from "react-icons/hi";

import { URLS } from "MVVM/Utilities/Maps";
// Per-section rail art: a background that fills the pitch column and a glass
// object that sits with the headline.
import buildBg from "MVVM/Assets/Nav/MegaMenu/build_bg.jpg";
import buildGlass from "MVVM/Assets/Nav/MegaMenu/build_glass.png";
import captureBg from "MVVM/Assets/Nav/MegaMenu/capture_bg.jpg";
import captureGlass from "MVVM/Assets/Nav/MegaMenu/capture_glass.png";
import competeBg from "MVVM/Assets/Nav/MegaMenu/compete_bg.jpg";
import competeGlass from "MVVM/Assets/Nav/MegaMenu/compete_glass.png";
import connectBg from "MVVM/Assets/Nav/MegaMenu/connect_bg.jpg";
import connectGlass from "MVVM/Assets/Nav/MegaMenu/connect_glass.png";
import editBg from "MVVM/Assets/Nav/MegaMenu/edit_bg.jpg";
import editGlass from "MVVM/Assets/Nav/MegaMenu/edit_glass.png";
// The old sidebar's own mark, which reads better here than a generic clock.
import { ReactComponent as HistoryLogo } from "MVVM/Assets/Nav/nav-history.svg";
import { ReactComponent as StudioLogo } from "MVVM/Assets/Nav/nav-studio.svg";

const DEV_PORTAL_URL = "https://developer.allstar.gg/auth/allstar";
// The Partner panel points at the portal's own landing page; the Developer API
// item still deep-links into auth for someone who knows what they want.
const DEV_PORTAL_LANDING = "https://developer.allstar.gg";

// Either a react-icons glyph or one of the bundled SVG assets.
export type TNavIcon = IconType | ComponentType<SVGProps<SVGSVGElement>>;

export interface INavItem {
  title: string;
  description: string;
  badge?: string;
  icon: TNavIcon;
  to?: string;
  href?: string;
  requiresLogin?: boolean;
  // Setup explainers: useful to a visitor, noise to someone already set up.
  hideWhenLoggedIn?: boolean;
}

// The panel's funnel CTA, in the left third. Logged out it sells, logged in it
// deep-links into the tool.
interface INavCta {
  label: string;
  labelLoggedIn: string;
  to?: string;
  toLoggedIn?: string;
  href?: string;
  // Signed out, some destinations are behind a PrivateRoute, where a plain link
  // means a login wall. A tag rather than a component reference, like
  // `featured` below, so this file stays data-only.
  modalLoggedOut?: "steambot";
  // Where a CTA asks the user to set something up, where it points once they
  // have. Only Capture has a setup step, so only Capture sets these.
  labelDone?: string;
  toDone?: string;
}

export interface INavCategory {
  key: string;
  label: string;
  // Mobile drawer only, like TNavBarEntry's: the desktop bar keeps bare labels.
  icon: TNavIcon;
  eyebrow: string;
  blurb: string;
  cta: INavCta;
  // Flat list, also used for active-route matching.
  items: INavItem[];
  // Fills the pitch column behind the copy.
  art?: string;
  // Glass object that sits with the headline, over the background.
  glass?: string;
  // Tints the CTA to match the section's artwork, so the button belongs to the
  // rail it sits on rather than being green on five different backgrounds.
  accent?: string;
  // Live content rendered above the item grid. Only one kind so far, so this
  // is a tag rather than a component reference: links.ts stays data-only.
  featured?: "competition";
}

// The desktop panel applies this twice and the mobile sheet once, so it is
// declared here rather than three times over.
export const visibleItems = (items: INavItem[], isLoggedIn: boolean) =>
  items.filter((item) =>
    isLoggedIn ? !item.hideWhenLoggedIn : !item.requiresLogin,
  );

// Every destination is a live route. Design-doc items without one (Past
// Winners, Leaderboards, API Docs, Templates and the rest) are omitted rather
// than shipped as dead links.
const ITEMS = {
  freeClips: {
    title: "Free Clips",
    description: "Type !allstar in chat and the play comes back as a clip",
    icon: HiGift,
    to: URLS.ALLSTAR_COMMAND,
  },
  steamBot: {
    title: "Steam Bot",
    description:
      "Add our bot and Valve matches are picked up the moment they end",
    badge: "New",
    icon: FaSteam,
    to: URLS.STEAM_BOT,
    // /steam-bot is a PrivateRoute, so signed out this row would be a login
    // wall. The panel's CTA sells the bot to that visitor instead.
    requiresLogin: true,
  },
  autocapture: {
    title: "Autocapture",
    description: "Clips land in your library after every match",
    badge: "New",
    icon: HiVideoCamera,
    to: URLS.AUTOCAPTURE,
  },
  howItWorks: {
    title: "How it Works",
    description: "Connect an account, play, clips appear",
    icon: HiQuestionMarkCircle,
    to: URLS.START,
    hideWhenLoggedIn: true,
  },
  fragmovie: {
    title: "Fragmovie",
    description: "Pick the plays, pick the track, we handle the edit",
    icon: GiDesertEagle,
    to: URLS.FRAGMOVIE,
  },
  montages: {
    title: "Montages",
    description: "String your best rounds into one video",
    icon: HiFilm,
    to: "/feature/montages",
  },
  studio: {
    title: "Studio",
    description: "Pick the effects and loadouts applied to your clips",
    icon: StudioLogo,
    to: URLS.STUDIO,
  },
  matchHistory: {
    title: "Match History",
    description: "Turn any match into clips, fragmovies or montages",
    icon: HistoryLogo,
    to: URLS.MATCH_HISTORY,
  },
  browseCompetitions: {
    title: "Competitions",
    description: "Weekly clip contests with skins and prize pools",
    icon: IoTrophy,
    to: URLS.COMPETITION,
  },
  createCompetition: {
    title: "Create a Competition",
    description: "Run your own contest for your community",
    icon: HiPlusCircle,
    to: "/competitions/create",
  },
  teams: {
    title: "Teams",
    description: "Squad libraries and shared clip pools",
    icon: HiUserGroup,
    to: URLS.TEAMS,
  },
  developerApi: {
    title: "Developer API",
    description: "Build on Allstar clips and match data",
    icon: HiCode,
    href: DEV_PORTAL_URL,
  },
  referralProgram: {
    title: "Partner Referral Program",
    description: "Earn rewards when friends subscribe or upgrade",
    badge: "Beta",
    icon: HiGift,
    to: "/referrals",
  },
  partnerProgram: {
    title: "Partner Program",
    description: "Perks and revenue share for creators",
    icon: HiSparkles,
    to: "/partners",
  },
  explore: {
    title: "Explore",
    description: "The feed of what everyone is making right now",
    icon: HiGlobeAlt,
    to: "/",
  },
  topPlayers: {
    title: "Top Players",
    description: "Every pro clip Allstar has captured, by player",
    icon: HiStar,
    to: URLS.TOP_PLAYERS,
  },
  blog: {
    title: "Blog",
    description: "Release notes, updates and announcements",
    icon: HiBookOpen,
    to: "/blog",
  },
  allstarBot: {
    title: "AllstarBot",
    description: "Your highlights delivered to your Discord server",
    icon: FaDiscord,
    to: "/allstarbot",
  },
} satisfies Record<string, INavItem>;

// One structure for both auth states: signing in adds the strip above rather
// than swapping the bar. Panels adapt via labelLoggedIn/toLoggedIn.
export const NAV_CATEGORIES: INavCategory[] = [
  {
    key: "capture",
    icon: HiVideoCamera,
    accent: "#4dbd94",
    label: "Capture",
    eyebrow: "Start from a match",
    blurb:
      "Everything starts in your match history. Pick a match, then pick a tool.",
    cta: {
      label: "Add the Steam bot",
      labelLoggedIn: "Add the Steam bot",
      toLoggedIn: URLS.STEAM_BOT,
      modalLoggedOut: "steambot",
      // Nothing left to add once the bot is connected, so it points at what the
      // bot feeds instead.
      labelDone: "Open Match History",
      toDone: URLS.MATCH_HISTORY,
    },
    art: captureBg,
    glass: captureGlass,
    // The three routes a clip can arrive by: manual, automatic, chat command.
    items: [
      ITEMS.matchHistory,
      ITEMS.steamBot,
      ITEMS.autocapture,
      ITEMS.freeClips,
      ITEMS.howItWorks,
    ],
  },
  {
    key: "edit",
    icon: HiPencilAlt,
    accent: "#7d6ce0",
    label: "Edit",
    eyebrow: "Do more with them",
    blurb:
      "Turn raw clips into montages and movies, in the browser, with no software to install.",
    cta: {
      label: "Open Studio",
      labelLoggedIn: "Open Studio",
      to: URLS.STUDIO,
      toLoggedIn: URLS.STUDIO,
    },
    art: editBg,
    glass: editGlass,
    // Studio is here rather than under Capture because it shapes how a clip
    // looks, not whether you get one.
    items: [ITEMS.fragmovie, ITEMS.montages, ITEMS.studio],
  },
  {
    key: "compete",
    icon: IoTrophy,
    accent: "#c0574a",
    label: "Compete",
    eyebrow: "Put them up against the best",
    blurb:
      "Weekly contests with skins and prize pools, or run your own for your community.",
    cta: {
      label: "Browse competitions",
      labelLoggedIn: "Browse competitions",
      to: URLS.COMPETITION,
      toLoggedIn: URLS.COMPETITION,
    },
    art: competeBg,
    glass: competeGlass,
    featured: "competition",
    items: [ITEMS.browseCompetitions, ITEMS.createCompetition],
  },
  {
    key: "connect",
    icon: HiShare,
    accent: "#4a8fd0",
    label: "Connect",
    eyebrow: "Share your best",
    blurb: "Where clips get seen, and what you can plug Allstar into.",
    cta: {
      label: "Browse Teams",
      labelLoggedIn: "Browse Teams",
      to: URLS.TEAMS,
      toLoggedIn: URLS.TEAMS,
    },
    art: connectBg,
    glass: connectGlass,
    items: [
      ITEMS.explore,
      ITEMS.topPlayers,
      ITEMS.teams,
      ITEMS.allstarBot,
      ITEMS.blog,
    ],
  },
  {
    key: "partner",
    icon: HiCode,
    accent: "#3f8f8a",
    label: "Partner",
    eyebrow: "Work with us",
    blurb:
      "Earn referral rewards, partner as a creator, or build with our API.",
    cta: {
      label: "Open the Developer Portal",
      labelLoggedIn: "Open the Developer Portal",
      href: DEV_PORTAL_LANDING,
    },
    art: buildBg,
    glass: buildGlass,
    items: [ITEMS.referralProgram, ITEMS.partnerProgram, ITEMS.developerApi],
  },
];

/**
 * One ordered list so links and menus can interleave. A leading/trailing split
 * cannot express "menu, link, menu", which is the order we want.
 *
 * The icon is for the mobile drawer only: the desktop bar keeps its labels bare
 * so the utility links stay the quiet ones.
 */
type TNavBarEntry =
  | { kind: "link"; label: string; to: string; icon?: TNavIcon }
  | { kind: "menu"; key: string };

/**
 * Identical in both auth states. Personal destinations are not here: they live
 * in the super nav strip, which is what makes signed in a superset of signed
 * out rather than a replacement for it.
 */
export const NAV_BAR: TNavBarEntry[] = [
  { kind: "menu", key: "capture" },
  { kind: "menu", key: "edit" },
  { kind: "menu", key: "compete" },
  { kind: "menu", key: "connect" },
  { kind: "menu", key: "partner" },
];

// /profile redirects to /u/:username, so linking to it meant the active check
// never matched once you landed.
export const profilePath = (username?: string) =>
  username ? `/u/${username}` : "/profile";

export type TShortcutKey = "teams" | "matches" | "library" | "profile";

export interface IPersonalLink {
  key: TShortcutKey;
  label: string;
  to: string;
  icon: TNavIcon;
}

/**
 * The personal destinations, in one place: the account panel lists all of them
 * with a pin, and the strip shows whichever are pinned.
 *
 * Order is least important first, because SuperNavLinks drops its leading
 * children as the viewport narrows: whatever sits last stays longest.
 */
export const personalLinks = (username?: string): IPersonalLink[] => [
  { key: "teams", label: "My Teams", to: URLS.TEAMS, icon: HiUserGroup },
  {
    key: "matches",
    label: "My Matches",
    to: URLS.MATCH_HISTORY,
    icon: HistoryLogo,
  },
  { key: "library", label: "My Library", to: "/library", icon: HiCollection },
  {
    key: "profile",
    label: "My Profile",
    to: profilePath(username),
    icon: HiUser,
  },
];

// Teams is the one left out: it is the least used of the four, and an empty
// pin invites the user to notice the row is theirs to arrange.
export const DEFAULT_SHORTCUTS: TShortcutKey[] = [
  "matches",
  "library",
  "profile",
];

export const DISCORD_INVITE_URL = "https://discord.gg/playsharestar";
export const SUPPORT_URL = "https://help.allstar.gg/hc/en-us/requests/new";

/**
 * Route matching for the nav's active state. Prefix-aware so /competitions/123
 * still lights up Competitions, and "/" only ever matches itself so a root
 * link cannot claim every page.
 */
export const isPathActive = (to: string | undefined, pathname: string) => {
  if (!to) return false;
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
};

export type TNavDestination =
  | { kind: "to"; to: string }
  | { kind: "href"; href: string }
  | { kind: "modal"; modal: "steambot" };

export interface INavCtaState {
  isLoggedIn: boolean;
  // Capture's setup step. Once it is done its CTA stops asking for it.
  steambotEnrolled?: boolean;
}

/**
 * The label and destination a category resolves to, for the bar's own label and
 * for the panel's CTA. One function so the two cannot drift: the label always
 * describes the place the click actually goes.
 */
export const resolveCta = (
  { cta }: INavCategory,
  { isLoggedIn, steambotEnrolled }: INavCtaState,
): { label: string; destination: TNavDestination | null } => {
  if (isLoggedIn && steambotEnrolled && cta.toDone)
    return {
      label: cta.labelDone || cta.labelLoggedIn,
      destination: { kind: "to", to: cta.toDone },
    };

  const label = isLoggedIn ? cta.labelLoggedIn : cta.label;

  if (!isLoggedIn && cta.modalLoggedOut)
    return { label, destination: { kind: "modal", modal: cta.modalLoggedOut } };
  if (cta.href) return { label, destination: { kind: "href", href: cta.href } };

  const to = isLoggedIn ? cta.toLoggedIn : cta.to;
  return { label, destination: to ? { kind: "to", to } : null };
};

export const findActiveCategoryKey = (
  pathname: string,
  categories: INavCategory[] = NAV_CATEGORIES,
) =>
  categories.find((category) =>
    category.items.some((item) => isPathActive(item.to, pathname)),
  )?.key || null;
